import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { ExpandMore, CheckCircleOutline } from '@material-ui/icons';
import _ from 'lodash';
import styles from './styles';
import { isMobile } from '../../../../lib/getDeviceSize';

export const ROOM_TYPES = [
  { label: 'Bathroom', rt: 'bathroom' },
  { label: 'Bedroom', rt: 'bedroom' },
  { label: 'Dining Room', rt: 'dining_area' },
  { label: 'Home Exterior', rt: 'home_exterior' },
  { label: 'Kitchen', rt: 'kitchen' },
  { label: 'Living Room', rt: 'living_room' },
  { label: 'Outdoor Space', rt: 'outdoor_space' },
];

export interface Props {
  defaultValue: any;
  handleQualityFilter: Function;
}

const StyledAccordion = withStyles({
  root: {
    boxShadow: 'none',
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      margin: 'auto',
    },
  },
  expanded: {},
})(Accordion);

const StyledAccordionSummary = withStyles({
  root: {
    backgroundColor: 'white',
    borderBottom: '1px solid rgba(0, 0, 0, .125)',
    padding: 0,
    minHeight: 56,
    '&$expanded': {
      minHeight: 56,
    },
  },
  content: {
    '&$expanded': {
      margin: '12px 0',
    },
  },
  expanded: {},
  expandIcon: {
    padding: 10,
  },
})(AccordionSummary);

const StyledAccordionDetails = withStyles(theme => ({
  root: {
    padding: '10px 0',
  },
}))(AccordionDetails);

function RoomCondition(props: Props) {
  const classes: any = styles(props);
  const [selectedRoomType, setSelectedRoomType] = useState<any>([]);
  const [selectedRoomCondition, setRoomCondition] = useState<any>(
    props.defaultValue,
  );
  const [filterButtonSize, setButtonSize] = useState(100);
  const ref = useRef<any>(null);

  useEffect(() => {
    const buttonSize = (ref.current ? ref.current.offsetWidth : 400) / 3;
    setButtonSize(buttonSize);
  }, [ref.current]);

  useEffect(() => {
    setRoomCondition(props.defaultValue);

    if (props.defaultValue.length === 0) {
      setSelectedRoomType(
        props.defaultValue.map((selectedRoom: any) => selectedRoom.rt),
      );
    }
  }, [props.defaultValue]);

  useEffect(() => {
    if (props.defaultValue.length !== 0) {
      setSelectedRoomType(
        props.defaultValue.map((selectedRoom: any) => selectedRoom.rt),
      );
    }
  }, []);

  useEffect(() => {
    // Eliminate values with no "rc" value ex. [{"rt":"bedroom","rc":[]},{"rt":"bathroom","rc":[]}];
    const value = selectedRoomCondition.filter(
      (roomType: any) => roomType.rc.length,
    );
    props.handleQualityFilter(value);
  }, [selectedRoomCondition]);

  const toggleOption = useCallback((panel: string) => {
    setSelectedRoomType((selectedRoomTypeLocal: any) => {
      if (selectedRoomTypeLocal.includes(panel)) {
        return [
          ...selectedRoomTypeLocal.filter(
            (selectedRoom: any) => selectedRoom !== panel,
          ),
        ];
      } else {
        return [...selectedRoomTypeLocal, panel];
      }
    });
  }, []);

  const onQualitySelect = useCallback(
    (value: number, selectedRoomTypeLocal: string) => {
      // Doing this way so we dont tamper source value
      const roomCopy = JSON.parse(JSON.stringify(selectedRoomCondition));
      // Check if the room type already selected
      const existingRoomCondition = roomCopy.find(
        (room: any) => room.rt === selectedRoomTypeLocal,
      );
      const existingRoomIndex = selectedRoomCondition.findIndex(
        (room: any) => room.rt === selectedRoomTypeLocal,
      );
      if (!existingRoomCondition) {
        // If new room, add to the selected room condition
        const newRoomCondition = { rt: selectedRoomTypeLocal, rc: [value] };
        setRoomCondition((prevState: any) => [...prevState, newRoomCondition]);
      } else {
        if (existingRoomCondition.rc.includes(value)) {
          // Remove existing quality type
          const newRc = _.difference(existingRoomCondition.rc, [value]);
          existingRoomCondition.rc = newRc;
        } else {
          // New quality type
          existingRoomCondition.rc.push(value);
        }
        const finalData = Object.assign([], roomCopy, {
          [existingRoomIndex]: existingRoomCondition,
        });
        setRoomCondition(finalData);
      }
    },
    [selectedRoomCondition],
  );

  const renderQualityButtons = useCallback(
    (type: string) => {
      const selectedType = selectedRoomCondition.find(
        (room: any) => room.rt === type,
      );
      let selectedQuality: any = {};
      if (selectedType && selectedType.rc.length > 0) {
        if (selectedType.rc.includes(0)) {
          selectedQuality.poor = true;
        }
        if (selectedType.rc.includes(1)) {
          selectedQuality.average = true;
        }
        if (selectedType.rc.includes(2)) {
          selectedQuality.good = true;
        }
      }
      return (
        <React.Fragment>
          <Button
            className={`${classes.roomQualityButton} ${
              selectedQuality.poor ? classes.selectedQuality : ''
            }`}
            style={{
              borderTopLeftRadius: 10,
              borderBottomLeftRadius: 10,
              width: filterButtonSize - 10,
            }}
            onClick={() => onQualitySelect(0, type)}
          >
            <Typography
              className={
                selectedQuality.poor
                  ? classes.selectedText
                  : classes.roomQualityButtonText
              }
              style={{
                fontSize: isMobile() ? 14 : selectedQuality.poor ? 14 : 16,
              }}
            >
              Fixer Finder{' '}
              {selectedQuality.poor && (
                <CheckCircleOutline
                  style={{ paddingLeft: 5 }}
                  htmlColor="white"
                  fontSize="small"
                />
              )}
            </Typography>
          </Button>
          <Button
            className={`${classes.roomQualityButton} ${
              selectedQuality.average ? classes.selectedQuality : ''
            }`}
            style={{
              width: filterButtonSize - 10,
            }}
            onClick={() => onQualitySelect(1, type)}
          >
            <Typography
              className={
                selectedQuality.average
                  ? classes.selectedText
                  : classes.roomQualityButtonText
              }
            >
              Average{' '}
              {selectedQuality.average && (
                <CheckCircleOutline
                  style={{ paddingLeft: 5 }}
                  htmlColor="white"
                  fontSize="small"
                />
              )}
            </Typography>
          </Button>
          <Button
            className={`${classes.roomQualityButton} ${
              selectedQuality.good ? classes.selectedQuality : ''
            }`}
            style={{
              borderTopRightRadius: 10,
              borderBottomRightRadius: 10,
              width: filterButtonSize - 10,
            }}
            onClick={() => onQualitySelect(2, type)}
          >
            <Typography
              className={
                selectedQuality.good
                  ? classes.selectedText
                  : classes.roomQualityButtonText
              }
            >
              Great{' '}
              {selectedQuality.good && (
                <CheckCircleOutline
                  style={{ paddingLeft: 5 }}
                  htmlColor="white"
                  fontSize="small"
                />
              )}
            </Typography>
          </Button>
        </React.Fragment>
      );
    },
    [selectedRoomCondition, filterButtonSize, onQualitySelect],
  );

  return (
    <div className={classes.roomConditionContainer}>
      <div ref={ref} style={{ width: '100%' }}>
        {ROOM_TYPES.map((option, index) => {
          const onCardPress = () => toggleOption(option.rt);
          return (
            <StyledAccordion
              key={option.rt}
              expanded={selectedRoomType.includes(option.rt)}
              onChange={onCardPress}
            >
              <StyledAccordionSummary
                aria-controls={`${option.label}-content`}
                id={`${option.label}-header`}
                expandIcon={<ExpandMore />}
                // classes={{ expandIcon: classes.expandIcon }}
              >
                <Typography>{option.label}</Typography>
              </StyledAccordionSummary>
              <StyledAccordionDetails style={{ justifyContent: 'center' }}>
                {renderQualityButtons(option.rt)}
              </StyledAccordionDetails>
            </StyledAccordion>
          );
        })}
      </div>
    </div>
  );
}

export default RoomCondition;
